import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';

// Helper functions
const getSupplierName = (supplierId, supplierNamesData) => {
if (!supplierNamesData || !Array.isArray(supplierNamesData)) return 'Unknown';
const supplier = supplierNamesData.find(s => parseInt(s.id) === parseInt(supplierId));
return supplier ? supplier.name : 'Unknown';
};

const getCounterName = (counterId, counterNamesData) => {
if (!counterNamesData || !Array.isArray(counterNamesData)) return 'Unknown';
const counter = counterNamesData.find(c => parseInt(c.id) === parseInt(counterId));
return counter ? counter.name : 'Unknown';
};

const getStockName = (stockId, stockNamesData) => {
if (!stockNamesData || !Array.isArray(stockNamesData)) return 'Unknown';
const stock = stockNamesData.find(s => parseInt(s.id) === parseInt(stockId));
return stock ? stock.name : 'Unknown';
};

const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US').format(amount);
};

const formatDate = (dateString) => {
return new Date(dateString).toLocaleDateString();
};

// Calculate profit for an item
const calculateProfit = (item) => {
return (item.selling - item.cost) * item.quantity;
};

// Calculate total value for an item
const calculateTotalValue = (item) => {
return item.quantity * item.selling;
};

// Manual table drawing function for PDF
const drawReportTable = (doc, data, startY, reportType) => {
const pageWidth = doc.internal.pageSize.width;
const margin = 14;
const rowHeight = 10;

let colWidths = [];
let headers = [];

switch (reportType) {
    case 'detailed':
    colWidths = [8, 20, 25, 25, 30, 15, 20, 20, 25, 25, 20];
    headers = ['#', 'Date', 'Supplier', 'Counter', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    break;
    case 'supplier':
    colWidths = [10, 35, 15, 20, 25, 25, 25];
    headers = ['#', 'Supplier', 'Items', 'Quantity', 'Total Cost', 'Total Value', 'Profit'];
    break;
    case 'counter':
    colWidths = [10, 35, 15, 20, 25, 25, 25];
    headers = ['#', 'Counter', 'Items', 'Quantity', 'Total Cost', 'Total Value', 'Profit'];
    break;
    case 'stock':
    colWidths = [10, 30, 25, 15, 20, 20, 25, 25];
    headers = ['#', 'Stock Item', 'Supplier', 'Quantity', 'Avg Cost', 'Avg Selling', 'Total Value', 'Profit'];
    break;
    default:
    colWidths = [10, 25, 25, 30, 15, 20, 20, 25];
    headers = ['#', 'Supplier', 'Counter', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Value'];
}

let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;
if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
}

// Draw table header
doc.setFillColor(255, 255, 255);
doc.setTextColor(25, 118, 210);
doc.setFont(undefined, 'bold');

let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

// Draw header underline
doc.setDrawColor(25, 118, 210);
doc.setLineWidth(0.5);
doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    const actualRowHeight = rowHeight;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
    
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    doc.setDrawColor(25, 118, 210);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row data based on report type
    let cellIndex = 0;
    const cells = [];
    
    switch (reportType) {
    case 'detailed':
        cells.push(
        (rowIndex + 1).toString(),
        formatDate(row.stock_date),
        row.supplier_name,
        row.counter_name,
        row.stock_name,
        formatCurrency(row.quantity),
        formatCurrency(row.cost),
        formatCurrency(row.selling),
        formatCurrency(row.quantity * row.cost),
        formatCurrency(row.quantity * row.selling),
        formatCurrency(calculateProfit(row))
        );
        break;
    case 'supplier':
        cells.push(
        (rowIndex + 1).toString(),
        row.supplier_name,
        formatCurrency(row.items_count),
        formatCurrency(row.total_quantity),
        formatCurrency(row.total_cost),
        formatCurrency(row.total_value),
        formatCurrency(row.total_profit)
        );
        break;
    case 'counter':
        cells.push(
        (rowIndex + 1).toString(),
        row.counter_name,
        formatCurrency(row.items_count),
        formatCurrency(row.total_quantity),
        formatCurrency(row.total_cost),
        formatCurrency(row.total_value),
        formatCurrency(row.total_profit)
        );
        break;
    case 'stock':
        cells.push(
        (rowIndex + 1).toString(),
        row.stock_name,
        row.supplier_name,
        formatCurrency(row.total_quantity),
        formatCurrency(row.avg_cost),
        formatCurrency(row.avg_selling),
        formatCurrency(row.total_value),
        formatCurrency(row.total_profit)
        );
        break;
    default:
        cells.push(
        (rowIndex + 1).toString(),
        row.supplier_name,
        row.counter_name,
        row.stock_name,
        formatCurrency(row.quantity),
        formatCurrency(row.cost),
        formatCurrency(row.selling),
        formatCurrency(calculateTotalValue(row))
        );
    }
    
    cells.forEach((cell, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    doc.text(cell.toString(), xPos + 2, currentY + 7);
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Excel export for Club Inventory Report
export const exportClubInventoryReportToExcel = (reportData, filters, referenceData) => {
try {
    const { summary, details, topSuppliers, topCounters, topStockItems, lowQuantityItems, highValueItems } = reportData;
    const { supplierNames = [], counterNames = [], stockNames = [] } = referenceData;

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
    ['CLUB INVENTORY REPORT SUMMARY'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['OVERALL SUMMARY'],
    ['Total Items:', summary.total_items],
    ['Total Quantity:', summary.total_quantity],
    ['Total Cost Value:', formatCurrency(summary.total_cost_value)],
    ['Total Selling Value:', formatCurrency(summary.total_selling_value)],
    ['Total Profit:', formatCurrency(summary.total_profit)],
    ['Average Cost per Item:', formatCurrency(summary.average_cost_per_item)],
    ['Average Selling per Item:', formatCurrency(summary.average_selling_per_item)],
    ['Suppliers Count:', summary.suppliers_count],
    ['Counters Count:', summary.counters_count],
    ['Stock Items Count:', summary.stock_items_count],
    [],
    ['PROFITABILITY ANALYSIS'],
    ['Profit Margin:', summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    ['Cost Percentage:', summary.total_selling_value > 0 ? `${((summary.total_cost_value / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    [],
    ['APPLIED FILTERS'],
    filters.dateFrom && filters.dateTo ? [`Date Range: ${filters.dateFrom} to ${filters.dateTo}`] : [],
    filters.supplier_id ? [`Supplier: ${getSupplierName(filters.supplier_id, supplierNames)}`] : [],
    filters.counter_id ? [`Counter: ${getCounterName(filters.counter_id, counterNames)}`] : [],
    filters.stock_id ? [`Stock Item: ${getStockName(filters.stock_id, stockNames)}`] : [],
    filters.min_quantity ? [`Minimum Quantity: ${filters.min_quantity}`] : [],
    filters.max_quantity ? [`Maximum Quantity: ${filters.max_quantity}`] : []
    ].filter(row => row.length > 0);

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Detailed Data Sheet
    if (details && details.length > 0) {
    const detailedHeaders = ['ID', 'Stock Date', 'Supplier', 'Counter', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    const detailedData = details.map((item, index) => [
        item.id,
        formatDate(item.stock_date),
        item.supplier_name,
        item.counter_name,
        item.stock_name,
        item.quantity,
        item.cost,
        item.selling,
        item.quantity * item.cost,
        item.quantity * item.selling,
        (item.selling - item.cost) * item.quantity
    ]);

    const detailedSheet = XLSX.utils.aoa_to_sheet([detailedHeaders, ...detailedData]);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, 'Detailed Data');
    }

    // Top Suppliers Sheet
    if (topSuppliers && topSuppliers.length > 0) {
    const supplierHeaders = ['Supplier', 'Items Count', 'Total Quantity', 'Total Cost', 'Total Value', 'Total Profit'];
    const supplierData = topSuppliers.map(supplier => [
        supplier.supplier_name,
        supplier.items_count,
        supplier.total_quantity,
        supplier.total_cost,
        supplier.total_value,
        supplier.total_profit
    ]);

    const supplierSheet = XLSX.utils.aoa_to_sheet([supplierHeaders, ...supplierData]);
    XLSX.utils.book_append_sheet(workbook, supplierSheet, 'Top Suppliers');
    }

    // Top Counters Sheet
    if (topCounters && topCounters.length > 0) {
    const counterHeaders = ['Counter', 'Items Count', 'Total Quantity', 'Total Cost', 'Total Value', 'Total Profit'];
    const counterData = topCounters.map(counter => [
        counter.counter_name,
        counter.items_count,
        counter.total_quantity,
        counter.total_cost,
        counter.total_value,
        counter.total_profit
    ]);

    const counterSheet = XLSX.utils.aoa_to_sheet([counterHeaders, ...counterData]);
    XLSX.utils.book_append_sheet(workbook, counterSheet, 'Top Counters');
    }

    // High Value Items Sheet
    if (highValueItems && highValueItems.length > 0) {
    const highValueHeaders = ['Stock Item', 'Supplier', 'Quantity', 'Cost', 'Selling', 'Total Value', 'Profit'];
    const highValueData = highValueItems.map(item => [
        item.stock_name,
        item.supplier_name,
        item.quantity,
        item.cost,
        item.selling,
        item.total_value,
        item.profit
    ]);

    const highValueSheet = XLSX.utils.aoa_to_sheet([highValueHeaders, ...highValueData]);
    XLSX.utils.book_append_sheet(workbook, highValueSheet, 'High Value Items');
    }

    // Low Quantity Alerts Sheet
    if (lowQuantityItems && lowQuantityItems.length > 0) {
    const lowQtyHeaders = ['Stock Item', 'Supplier', 'Counter', 'Quantity', 'Cost', 'Selling', 'Status'];
    const lowQtyData = lowQuantityItems.map(item => [
        item.stock_name,
        item.supplier_name,
        item.counter_name,
        item.quantity,
        item.cost,
        item.selling,
        item.quantity <= 0 ? 'Out of Stock' : item.quantity <= 10 ? 'Low Stock' : 'Normal'
    ]);

    const lowQtySheet = XLSX.utils.aoa_to_sheet([lowQtyHeaders, ...lowQtyData]);
    XLSX.utils.book_append_sheet(workbook, lowQtySheet, 'Low Quantity Alerts');
    }

    // Generate file name
    const fileName = `club_inventory_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Club Inventory Report Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Club Inventory Report
export const exportClubInventoryReportToPDF = (reportData, filters, referenceData) => {
try {
    const { summary, details, topSuppliers, topCounters } = reportData;
    const { supplierNames = [], counterNames = [], stockNames = [] } = referenceData;

    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
    title: 'Club Inventory Analysis Report',
    subject: 'Club Inventory Detailed Report',
    author: companyInfo.name,
    keywords: 'club, inventory, analysis, report',
    creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('CLUB INVENTORY ANALYSIS REPORT', 105, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Report Type: ${filters.report_type || 'detailed'}`, 14, currentY);
    currentY += 6;
    
    if (filters.dateFrom && filters.dateTo) {
    doc.text(`Date Range: ${filters.dateFrom} to ${filters.dateTo}`, 14, currentY);
    currentY += 6;
    }
    
    if (filters.supplier_id) {
    doc.text(`Supplier: ${getSupplierName(filters.supplier_id, supplierNames)}`, 14, currentY);
    currentY += 6;
    }
    
    if (filters.counter_id) {
    doc.text(`Counter: ${getCounterName(filters.counter_id, counterNames)}`, 14, currentY);
    currentY += 6;
    }
    
    // Summary section
    doc.setFontSize(12);
    doc.setTextColor(25, 118, 210);
    doc.text('EXECUTIVE SUMMARY', 14, currentY + 10);
    currentY += 20;
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    
    // Summary in two columns
    const col1X = 14;
    const col2X = 105;
    
    doc.text(`Total Items: ${formatCurrency(summary.total_items)}`, col1X, currentY);
    doc.text(`Total Quantity: ${formatCurrency(summary.total_quantity)}`, col2X, currentY);
    currentY += 7;
    
    doc.text(`Total Cost Value: ${formatCurrency(summary.total_cost_value)}`, col1X, currentY);
    doc.text(`Total Selling Value: ${formatCurrency(summary.total_selling_value)}`, col2X, currentY);
    currentY += 7;
    
    doc.text(`Total Profit: ${formatCurrency(summary.total_profit)}`, col1X, currentY);
    doc.text(`Profit Margin: ${summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'}`, col2X, currentY);
    currentY += 7;
    
    doc.text(`Suppliers: ${summary.suppliers_count}`, col1X, currentY);
    doc.text(`Counters: ${summary.counters_count}`, col2X, currentY);
    currentY += 10;
    
    // Top Suppliers
    if (topSuppliers && topSuppliers.length > 0) {
    doc.setFontSize(11);
    doc.setTextColor(25, 118, 210);
    doc.text('TOP 5 SUPPLIERS BY VALUE', 14, currentY);
    currentY += 8;
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    
    topSuppliers.slice(0, 5).forEach((supplier, index) => {
        doc.text(`${index + 1}. ${supplier.supplier_name}`, 20, currentY);
        doc.text(formatCurrency(supplier.total_value), 180, currentY);
        currentY += 6;
    });
    
    currentY += 5;
    }
    
    // Top Counters
    if (topCounters && topCounters.length > 0) {
    doc.setFontSize(11);
    doc.setTextColor(25, 118, 210);
    doc.text('TOP 5 COUNTERS BY VALUE', 14, currentY);
    currentY += 8;
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    
    topCounters.slice(0, 5).forEach((counter, index) => {
        doc.text(`${index + 1}. ${counter.counter_name}`, 20, currentY);
        doc.text(formatCurrency(counter.total_value), 180, currentY);
        currentY += 6;
    });
    
    currentY += 5;
    }
    
    // Detailed Data Table
    if (details && details.length > 0) {
    doc.setFontSize(11);
    doc.setTextColor(25, 118, 210);
    doc.text('DETAILED INVENTORY DATA', 14, currentY + 5);
    currentY += 15;
    
    // Prepare table data
    const tableData = details.map((item, index) => [
        (index + 1).toString(),
        formatDate(item.stock_date),
        item.supplier_name.substring(0, 15),
        item.counter_name.substring(0, 15),
        item.stock_name.substring(0, 20),
        formatCurrency(item.quantity),
        formatCurrency(item.cost),
        formatCurrency(item.selling),
        formatCurrency(item.quantity * item.selling),
        formatCurrency((item.selling - item.cost) * item.quantity)
    ]);
    
    // Draw table
    drawReportTable(doc, details, currentY, 'detailed');
    }
    
    // Recommendations
    doc.setFontSize(11);
    doc.setTextColor(25, 118, 210);
    doc.text('KEY RECOMMENDATIONS', 14, 280);
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    
    const recommendations = [];
    
    if (summary.total_profit / summary.total_selling_value < 0.2) {
    recommendations.push('• Consider increasing selling prices for better profit margins');
    }
    
    if (summary.total_quantity / summary.total_items < 10) {
    recommendations.push('• Increase stock quantities for better inventory management');
    }
    
    recommendations.push('• Review high-value items for restocking opportunities');
    recommendations.push('• Monitor low-quantity items for timely reordering');
    
    recommendations.forEach((rec, index) => {
    doc.text(rec, 20, 285 + (index * 6));
    });
    
    const fileName = `club_inventory_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Club Inventory Report PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// CSV export for Club Inventory Report
export const exportClubInventoryReportToCSV = (reportData, filters, referenceData) => {
try {
    const { summary, details } = reportData;
    const { supplierNames = [], counterNames = [], stockNames = [] } = referenceData;

    // Create summary CSV
    const summaryCsv = [
    ['CLUB INVENTORY REPORT SUMMARY'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['Metric', 'Value'],
    ['Total Items', summary.total_items],
    ['Total Quantity', summary.total_quantity],
    ['Total Cost Value', summary.total_cost_value],
    ['Total Selling Value', summary.total_selling_value],
    ['Total Profit', summary.total_profit],
    ['Average Cost per Item', summary.average_cost_per_item],
    ['Average Selling per Item', summary.average_selling_per_item],
    ['Suppliers Count', summary.suppliers_count],
    ['Counters Count', summary.counters_count],
    ['Stock Items Count', summary.stock_items_count],
    ['Profit Margin', summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    [],
    ['FILTERS APPLIED'],
    filters.dateFrom && filters.dateTo ? ['Date Range', `${filters.dateFrom} to ${filters.dateTo}`] : [],
    filters.supplier_id ? ['Supplier', getSupplierName(filters.supplier_id, supplierNames)] : [],
    filters.counter_id ? ['Counter', getCounterName(filters.counter_id, counterNames)] : [],
    filters.stock_id ? ['Stock Item', getStockName(filters.stock_id, stockNames)] : []
    ].filter(row => row.length > 0);

    // Create detailed CSV
    let detailedCsv = [];
    if (details && details.length > 0) {
    const detailedHeaders = ['ID', 'Stock Date', 'Supplier', 'Counter', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    detailedCsv = [
        ['DETAILED INVENTORY DATA'],
        [],
        detailedHeaders,
        ...details.map(item => [
        item.id,
        formatDate(item.stock_date),
        item.supplier_name,
        item.counter_name,
        item.stock_name,
        item.quantity,
        item.cost,
        item.selling,
        item.quantity * item.cost,
        item.quantity * item.selling,
        (item.selling - item.cost) * item.quantity
        ])
    ];
    }

    // Combine all CSV data
    const allCsvData = [...summaryCsv, [], ...detailedCsv];
    
    const csvContent = allCsvData
    .map(row => row.map(cell => {
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
        return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
    }).join(','))
    .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `club_inventory_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Club Inventory Report CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Export multiple report types
export const exportClubInventoryReport = {
toExcel: exportClubInventoryReportToExcel,
toPDF: exportClubInventoryReportToPDF,
toCSV: exportClubInventoryReportToCSV
};